import { useState } from "react";
import Check from "@mui/icons-material/Check";
import SwapVert from "@mui/icons-material/SwapVert";
import {
  ButtonBase,
  Menu,
  MenuItem,
  TableCell,
  Typography,
  useTheme,
} from "@mui/material";

import type {
  PatientListSortBy,
  PatientListSortDirection,
} from "@thalamos/aspire-common";
import { defaultPatientListSort } from "@thalamos/aspire-common";

export type PatientListSort = {
  sortBy: PatientListSortBy;
  sortDirection: PatientListSortDirection;
};

export type SortOption = {
  direction: PatientListSortDirection;
  label: string;
  badge: string;
};

export type SortableColumnHeaderProps = {
  label: string;
  column: PatientListSortBy;
  options: SortOption[];
  sort: PatientListSort;
  onSortChange: (sort: PatientListSort) => void;
};

export function SortableColumnHeader({
  label,
  column,
  options,
  sort,
  onSortChange,
}: SortableColumnHeaderProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isSortedByThisColumn = sort.sortBy === column;
  const activeOption = isSortedByThisColumn
    ? options.find((option) => option.direction === sort.sortDirection)
    : undefined;

  const colour = isSortedByThisColumn
    ? theme.palette.primary.main
    : theme.palette.text.primary;

  return (
    <TableCell
      sortDirection={isSortedByThisColumn ? sort.sortDirection : false}
    >
      <ButtonBase
        aria-haspopup="menu"
        aria-expanded={anchorEl !== null}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.25em",
          color: colour,
          fontWeight: 700,
          fontSize: "0.875rem",
          borderRadius: "4px",
          padding: "0.1em 0.2em",
        }}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        {label}
        <SwapVert sx={{ fontSize: "1.1rem" }} />
        {activeOption && (
          <Typography
            component="span"
            sx={{ fontSize: "0.75rem", fontWeight: 700, color: colour }}
          >
            {activeOption.badge}
          </Typography>
        )}
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={() => {
          setAnchorEl(null);
        }}
        MenuListProps={{ "aria-label": `Sort by ${label.toLowerCase()}` }}
        slotProps={{
          paper: { sx: { borderRadius: "12px", minWidth: "13em" } },
        }}
      >
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: theme.palette.text.secondary,
            padding: "0.5em 1.15em",
          }}
        >
          SORT
        </Typography>

        {options.map((option) => {
          const isSelected =
            isSortedByThisColumn && sort.sortDirection === option.direction;

          return (
            <MenuItem
              key={option.direction}
              selected={isSelected}
              sx={{
                gap: "1.5em",
                color: isSelected ? theme.palette.primary.main : undefined,
                fontWeight: isSelected ? 700 : undefined,
              }}
              onClick={() => {
                setAnchorEl(null);
                onSortChange(
                  isSelected
                    ? defaultPatientListSort
                    : { sortBy: column, sortDirection: option.direction },
                );
              }}
            >
              {option.label}
              {isSelected && (
                <Check
                  sx={{
                    marginLeft: "auto",
                    fontSize: "1.1rem",
                    color: theme.palette.primary.main,
                  }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </TableCell>
  );
}
